import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EventConsentStatusRequest, EventConsentStatusResponse, EventsResponse, EventSummaryResponse, ManageOptingForEventRequest, MonthlyCashflowResponse, NextEvent, TransactionsResponse, WalletResponse, WeeklyForecastResponse } from '../models/dfs-events.models';
import { catchError, firstValueFrom, iif, map, Observable, of, switchMap } from 'rxjs';
import { User, UserInfo, UserInfoResponse } from '../models/user.models';
import { UtilService } from './util.service';
import { LoginService } from './login.service';
import { environment } from '../../environments/environment';
import { Meter } from '../models/meter.models';

@Injectable({
  providedIn: 'root'
})
export class DfsService {
  endPointURL = environment.baseUrl;

  constructor(private http: HttpClient, private utilService: UtilService, private loginService: LoginService) { 
  }

  getLoggedInEmail(): Observable<string> {
    return this.loginService.loginUserEmail;
  }

  getMeterDetails(): Observable<Meter[]>{
    // Currently we derive meter details from user info
    const url = `${environment.baseUrl}/dfs/v1/dfs-user-info`;
    return this.loginService.loginUserEmail.pipe(
      switchMap(email =>
        this.http.get<UserInfoResponse>(url, {
          params: { email }
        }).pipe(
          map(response => {
            // Find the user data for the specific email
            const meterData = response.data
            .filter(user => user.MeterNo)
            .map((user: UserInfo) => ({
              MeterNo: user.MeterNo,
              MeterType: user.MeterType,
              Address: user.Address,
              Organization: user.Organization,
              PostCode: user.PostCode,
              Email: user.Email,
              Name: `${user.Firstname} ${user.Lastname}`.trim(),
              Consent: user.Consent === 1 ? true : false
            }));
            return meterData;
          }),
          catchError(error => {
            console.error('Error fetching meter details:', error);
            throw error;
          })
        )
      )
    );
  }

  get isTpi(): Observable<boolean> {
    return this.loginService.getTpiStatus();
  }

  editUserInfo(user: User) {
    return this.http.post( `${this.endPointURL}/dfs/v1/dfs-user-edit`, user);
  }

  getEventStatus(nextEvent: NextEvent): Observable<NextEvent> {
    return this.loginService.loginUserEmail.pipe(
      switchMap(email => {
        const payload: EventConsentStatusRequest = {
          date: nextEvent.date,
          email: email,
          eventId: nextEvent.eventId!
        }
        return this.http.post<EventConsentStatusResponse>(`${this.endPointURL}/dfs/v1/dfs-event-status`, payload).pipe(
          map(response => {
            nextEvent.status = response.data.status;
            return nextEvent;
          })
        );
      })
    );
  }

  // getEventSummary(): Observable<EventSummaryResponse> {
  //   return this.http.get<EventSummaryResponse>(`${this.endPointURL}/dfs/v1/dfs-event-summary`, 
  //     { 
  //       params: 
  //       { 
  //         email: this.registerService.getLoggedInEmail() 
  //       } 
  //     }).pipe(
  //       switchMap(response => {
  //         if (response.nextEvent.eventId) {
  //           return this.getEventStatus(response.nextEvent).pipe(
  //             map(updatedNextEvent => ({ ...response, nextEvent: updatedNextEvent }))
  //           );
  //         }
  //         return of(response);
  //       }),
  //       map(response => response),
  //     );
  // }

    getEventSummary(): Observable<EventSummaryResponse> {
      return this.loginService.loginUserEmail.pipe(
        switchMap(email => 
          this.http.get<EventSummaryResponse>(`${this.endPointURL}/dfs/v1/dfs-event-summary`, 
            { 
              params: 
              { 
                email: email 
              } 
            })
        )
      );
    }

  getEvents(): Observable<EventsResponse> {
    return this.loginService.loginUserEmail.pipe(
      switchMap(email => {
        const payload = {
          email: email,
          offset: 0,
          limit: 100
        };
        return this.http.post<EventsResponse>(`${this.endPointURL}/dfs/v1/dfs-events`, payload);
      })
    );
  }

  getTransactions(): Observable<TransactionsResponse> {
    return this.loginService.loginUserEmail.pipe(
      switchMap(email => {
        const payload = {
          email: email,
          offset: 0,
          limit: 100
        }; 
        return this.http.post<TransactionsResponse>(`${this.endPointURL}/wallet/v1/transactions`, payload);
      })
    );
  }

  getWalletSummary(): Observable<WalletResponse> {
    return this.loginService.loginUserEmail.pipe(
      switchMap(email => {
        const payload = { 
          email: email 
        };
        return this.http.post<WalletResponse>(`${this.endPointURL}/wallet/v1/summary`, payload);
      })
    );
  }

  manageOptingForEvent(nextEvent: NextEvent, consent: boolean) {
    return this.loginService.loginUserEmail.pipe(
      switchMap(email => {
        const payload: ManageOptingForEventRequest = {
          emailAddress: email,
          eventDate: nextEvent.date,
          eventTime: nextEvent.startTime!
        };
        if (consent) {
          return this.http.post(`${this.endPointURL}/dfs/v1/dfs-opt-in`, payload);
        } else {
          return this.http.post(`${this.endPointURL}/dfs/v1/dfs-opt-out`, payload);
        }
      })
    );
  }
  
  // manageOptingForUser(consent: boolean) {
  //   const payload = {
  //     email: this.registerService.getLoggedInEmail()
  //   };

  //   if (consent) {
  //     return this.http.post(`${this.endPointURL}/dfs/v1/dfs-opt-in`, payload);
  //   } else {
  //     return this.http.post(`${this.endPointURL}/dfs/v1/dfs-opt-out`, payload);
  //   }
  // }

  // manageOptingForMeter(consent: boolean, mpan: string) {
  //   const payload = {
  //     mpan
  //   };
  //   if (consent) {
  //     return this.http.post(`${this.endPointURL}/dfs/v1/dfs-opt-in`, payload);
  //   } else {
  //     return this.http.post(`${this.endPointURL}/dfs/v1/dfs-opt-out`, payload);
  //   }
  // }

  withdrawFromWallet(amount: number) {
    return this.loginService.loginUserEmail.pipe(
      switchMap(email => {
        const payload = {
          email: email,
          amount,
          currency: 'GBP',
          payoutType: 'link'
        };
        return this.http.post(`${this.endPointURL}/wallet/v1/withdrawal-request`, payload);
      })
    );
  }

  cancelWithdrawalRequest(transactionId: string) {
    return this.loginService.loginUserEmail.pipe(
      switchMap(email => {
        const payload = {
          email: email,
          transactionId: transactionId,
          currency: 'GBP',
          payoutType: 'link'
        };
        return this.http.post(`${this.endPointURL}/wallet/v1/cancel-withdrawal-request`, payload);
      })
    );
  }

  getWeeklyForecast(mpan?: string): Observable<WeeklyForecastResponse> {
    return this.loginService.loginUserEmail.pipe(
      switchMap(email => {
        const payload: any = { email: email };
        if (mpan) {
          payload['mpan'] = mpan;
        }
        return this.http.post<WeeklyForecastResponse>(`${this.endPointURL}/dfs/v1/dfs-weekly-forecast`, payload);
      })
    );

   // Generate dummy data for weekly forecast
  // const dummyData: WeeklyForecastResponse = {
  //   status: 200,
  //   message: 'Weekly forecast fetched successfully',
  //   data: [
  //     { Period: 'Morning', EventDate: '2023-10-01', Forecast: 22.5 },
  //     { Period: 'Afternoon', EventDate: '2023-10-01', Forecast: 28.5 },
  //     { Period: 'Evening', EventDate: '2023-10-01', Forecast: 18.5 },
  //     { Period: 'Night', EventDate: '2023-10-01', Forecast: 12.5 },
  //     { Period: 'Morning', EventDate: '2023-10-02', Forecast: 24.5 },
  //     { Period: 'Afternoon', EventDate: '2023-10-02', Forecast: 29.5 },
  //     { Period: 'Evening', EventDate: '2023-10-02', Forecast: 19.5 },
  //     { Period: 'Night', EventDate: '2023-10-02', Forecast: 14.5 },
  //     { Period: 'Morning', EventDate: '2023-10-03', Forecast: 23.5 },
  //     { Period: 'Afternoon', EventDate: '2023-10-03', Forecast: 27.5 },
  //     { Period: 'Evening', EventDate: '2023-10-03', Forecast: 21.5 },
  //     { Period: 'Night', EventDate: '2023-10-03', Forecast: 16.5 },
  //     { Period: 'Morning', EventDate: '2023-10-04', Forecast: 26.5 },
  //     { Period: 'Afternoon', EventDate: '2023-10-04', Forecast: 31.5 },
  //     { Period: 'Evening', EventDate: '2023-10-04', Forecast: 22.5 },
  //     { Period: 'Night', EventDate: '2023-10-04', Forecast: 17.5 },
  //     { Period: 'Morning', EventDate: '2023-10-05', Forecast: 25.5 },
  //     { Period: 'Afternoon', EventDate: '2023-10-05', Forecast: 32.5 },
  //     { Period: 'Evening', EventDate: '2023-10-05', Forecast: 23.5 },
  //     { Period: 'Night', EventDate: '2023-10-05', Forecast: 18.5 },
  //     { Period: 'Morning', EventDate: '2023-10-06', Forecast: 27.5 },
  //     { Period: 'Afternoon', EventDate: '2023-10-06', Forecast: 33.5 },
  //     { Period: 'Evening', EventDate: '2023-10-06', Forecast: 24.5 },
  //     { Period: 'Night', EventDate: '2023-10-06', Forecast: 19.5 }
  //   ]
  // }
  //  return of(dummyData);
  }

  getMonthlyCashflow(): Observable<MonthlyCashflowResponse> {
    return this.loginService.loginUserEmail.pipe(
      switchMap(email => {
        const payload = { email: email };
        return this.http.post<MonthlyCashflowResponse>(
          `${this.endPointURL}/wallet/v1/monthly-cashflow`,
          payload
        );
      })
    );

    // return of({
    //   status: 200,
    //   message: 'Monthly cashflow fetched successfully',
    //   data: [
    //     { month: 'January', cashFlow: 1000 },
    //     { month: 'February', cashFlow: 1200 },
    //     { month: 'March', cashFlow: 1300 },
    //     { month: 'April', cashFlow: 1100 },
    //     { month: 'May', cashFlow: 1500 },
    //     { month: 'June', cashFlow: 1400 },
    //     { month: 'July', cashFlow: 1600 },
    //     { month: 'August', cashFlow: 1700 },
    //     { month: 'September', cashFlow: 1800 },
    //     { month: 'October', cashFlow: 1900 },
    //     { month: 'November', cashFlow: 2000 },
    //     { month: 'December', cashFlow: 2100 }
    //   ]
    // })
  }
}
